import React, { createContext, useState, useMemo, useContext, Dispatch, SetStateAction, useCallback } from 'react'
import { useCommonStyles, useDialogStyles } from '../../../utils/admin/StyleHook'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import Typography from '@material-ui/core/Typography'
import useT from '../../../i18ns/admin/useT'
import { AgentType, AgentWalletsManualDepositQuery, AgentWalletsRechargeQuery, AgentWalletsSummary, AgentWalletTransactionType } from '@golden/gdk-admin'
import { InitialFormFunc, useDialogHandleSubmit, useGetDataByPayload, useRedirectHandleBack } from '../../../utils/default/ComplexFlowHook'
import { ValueGetter, createDefaultFormState, FormValidation } from '../../../utils/default/FormHook'
import { getValueFromChangeEvent, getCashInputProps, getCodeInputProps } from '../../../utils/default/FormHelper'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import FormStateProvider from '../../default/form/FormStateProvider'
import { createValidateCash, createValidateNotEmpty } from '../../../utils/default/Validator'
import FormField from '../../default/form/FormField'
import NumberInput from '../../default/form/NumberInput'
import RequiredText from '../../default/form/RequiredText'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { Path } from '../route/route'
import agentWalletTransactionTypeName from '../../../constants/default/agentWalletTransactionTypeName'
import agentTypeName from '../../../constants/default/agentTypeName'

type AgentWalletAdjustType = AgentWalletTransactionType | '-'

export interface FormType {
  account: string
  transactionType: AgentWalletAdjustType
  amount: string
  memo: string
  code: string
}

const initialForm: InitialFormFunc<FormType> = (defaultForm) => ({
  account: '',
  transactionType: '-',
  amount: '',
  memo: '',
  code: '',
  ...defaultForm
})

export const formToRequest = (form: FormType) => ({
  account: form.account,
  transactionType: form.transactionType,
  amount: form.amount,
  code: form.code,
  memo: form.memo
})

const memoFormHelperTextProps = { error: true }

const getValueFromEvent: ValueGetter<FormType> = {
  account: getValueFromChangeEvent,
  transactionType: getValueFromChangeEvent,
  amount: getValueFromChangeEvent,
  code: getValueFromChangeEvent,
  memo: getValueFromChangeEvent
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const TextField = React.memo(MuiTextField)

const amountInputProps = {
  ...getCashInputProps(),
  decimalScale: 2,
  maxLength: undefined
}

const codeInputProps = getCodeInputProps()

interface PropTypes {
  requestContext: React.Context<[string, Dispatch<SetStateAction<string>>]>
}

const needCodeTypes: Array<AgentWalletTransactionType | '-'> = [AgentWalletTransactionType.RECHARGE, AgentWalletTransactionType.TOP_UP, AgentWalletTransactionType.MANUAL_DEPOSIT]
const CodeInput: React.FC = () => {
  const { t } = useT()
  const { value: formValue } = useContext(FormContext)
  const requireCode = useMemo(() => needCodeTypes.includes(formValue.transactionType), [formValue, needCodeTypes])

  if (!requireCode) return null
  return (
    <Grid item>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <FormField<FormType, TextFieldProps>
            context={FormContext}
            component={NumberInput}
            name="code"
            fullWidth
            inputProps={codeInputProps}
            label={t('common.googleCode')}
            placeholder={t('placeholder.inputGoogleCode2')}
            required
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const AccountAgentWalletCreateForm: React.FC<PropTypes> = (props) => {
  const { requestContext } = props

  const classes = useCommonStyles()
  const dialogClasses = useDialogStyles()
  const { t } = useT()
  const [account] = useContext(requestContext)
  const globalDialog = useGlobalDialog()
  const [loading, setLoading] = useState<boolean>(false)
  const [report, setReport] = useState<AgentWalletsSummary | null>(null)
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: Path.ACCOUNT_AGENT_WALLET
  })

  const defaultForm = useMemo(() => initialForm({ account, ...report }), [account, report])
  const pageFlow = usePageFlow()
  const gdk = useGDK()
  const payload = useMemo(() => ({ account }), [account])

  useGetDataByPayload({
    payload,
    gdkFunc: (payload) => gdk.agentWallet.getSummary(payload),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: AgentWalletsSummary) => {
      setReport(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: account !== ''
  })

  const isEmperor = useMemo(() => report?.agent_type === AgentType.EMPEROR, [report])

  const transactionTypeOptions = useMemo(() => ([
    { name: '-', value: '-' },
    ...(isEmperor
      ? [
          { name: t(agentWalletTransactionTypeName[AgentWalletTransactionType.RECHARGE]), value: AgentWalletTransactionType.RECHARGE },
          { name: t(agentWalletTransactionTypeName[AgentWalletTransactionType.REVOKE]), value: AgentWalletTransactionType.REVOKE },
        ]
      : [
          { name: t(agentWalletTransactionTypeName[AgentWalletTransactionType.TOP_UP]), value: AgentWalletTransactionType.TOP_UP },
          { name: t(agentWalletTransactionTypeName[AgentWalletTransactionType.DEDUCT]), value: AgentWalletTransactionType.DEDUCT }
        ]
    ),
    { name: t(agentWalletTransactionTypeName[AgentWalletTransactionType.MANUAL_DEPOSIT]), value: AgentWalletTransactionType.MANUAL_DEPOSIT }
  ]), [isEmperor])

  const validation = useMemo(() => {
    return {
      account: [],
      transactionType: [
        {
          func: (value) => {
            if (value === '-') {
              return { isPass: false, stop: true, newError: { transactionType: t('error.mustNotEmpty') } }
            }
            return { isPass: true, stop: false, newError: { transactionType: null } }
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      amount: [
        {
          func: createValidateNotEmpty('amount', t),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: createValidateCash('amount', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      code: [
        {
          func: (value, form, lastSubmitForm, error) => {
            if (needCodeTypes.includes(form.transactionType)) {
              return createValidateNotEmpty('code', t)(value, form, lastSubmitForm, error)
            } else {
              return { isPass: true, stop: false, newError: { code: null } }
            }
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      memo: []
    } as FormValidation<FormType>
  }, [t])
  const memoInputProps = useMemo(() => ({
    classes: { input: classes.memoField },
    inputProps: {
      placeholder: t('placeholder.inputMemo'),
      maxLength: 100
    }
  }), [classes.memoField, t])

  const confirmText = (transactionType: AgentWalletAdjustType) => {
    switch (transactionType) {
      case AgentWalletTransactionType.MANUAL_DEPOSIT:
        return t('dialog.confirmCreateManualDeposit')
      case AgentWalletTransactionType.RECHARGE:
        return t('dialog.confirmCreateRecharge')
      case AgentWalletTransactionType.REVOKE:
        return t('dialog.confirmCreateRevoke')
      case AgentWalletTransactionType.TOP_UP:
        return t('dialog.confirmCreateTopUp')
      case AgentWalletTransactionType.DEDUCT:
      default:
        return t('dialog.confirmCreateDeduct')
    }
  }

  const { handleSubmit } = useDialogHandleSubmit({
    dialogId: 'createTransfer',
    globalDialog,
    getChangeDialogConfig: useCallback((form: FormType) => {
      return createGlobalDialogConfig({
        showIcon: false,
        notUseTypo: true,
        message: (
          <Typography className={dialogClasses.text} align="center">
            {confirmText(form.transactionType)}[{form.account}][{form.amount}]
          </Typography>
        )
      })
    }, [t, dialogClasses]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.createSuccess')
    }), [t]),
    onSuccess: () => {
      setLoading(false)
    },
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    onError: () => {
      setLoading(false)
    },
    formToRequest,
    gdkFunc: (payload) => {
      setLoading(true)
      const data = {
        account: payload.account,
        amount: payload.amount,
        memo: payload.memo,
        ...((needCodeTypes.includes(payload.transactionType) ? { code: payload.code } : {}))
      }

      switch (payload.transactionType) {
        case AgentWalletTransactionType.RECHARGE:
          return gdk.agentWallet.recharge(data as AgentWalletsRechargeQuery)
        case AgentWalletTransactionType.REVOKE:
          return gdk.agentWallet.revoke(data)
        case AgentWalletTransactionType.TOP_UP:
          return gdk.agentWallet.agentTopUp(data as AgentWalletsRechargeQuery)
        case AgentWalletTransactionType.DEDUCT:
          return gdk.agentWallet.agentDeduct(data)
        case AgentWalletTransactionType.MANUAL_DEPOSIT:
          return gdk.agentWallet.agentManualDeposit(data as AgentWalletsManualDepositQuery)
        default:
          return gdk.agentWallet.recharge(payload)
      }
    },
    gdkFuncDependencies: [gdk],
    afterSuccessDialog: handleBack
  })
  if (account === '') return null
  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultForm}
      onSubmit={handleSubmit}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
    >
      <Paper>
        <Box padding={4}>
          <LoadingAndErrorFrame {...pageFlow.status}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Box
                  paddingY={1.25}
                  paddingX={2}
                  className={classes.pinkTitleBar}
                >
                  <Typography variant="h5">
                    {t('common.agentInformation')}
                  </Typography>
                </Box>
              </Grid>
              {report
                ? (
                <React.Fragment>
                  <Grid item>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={6} lg={3}>
                        <TextField
                          label={t('common.account')}
                          value={account}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6} lg={3}>
                        <TextField
                          label={t('common.accountType')}
                          value={t(agentTypeName[report.agent_type])}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} lg={3} container alignItems="center">
                        <NumberInput
                          label={t('common.accountBalance')}
                          value={report.balance}
                          inputProps={amountInputProps}
                          disabled
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormField<FormType, DropDownProps>
                          context={FormContext}
                          component={DropDown}
                          name="transactionType"
                          options={transactionTypeOptions}
                          label={t('common.transactionType')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={8} lg={3}>
                        <FormField<FormType, TextFieldProps>
                          context={FormContext}
                          component={NumberInput}
                          name="amount"
                          label={t('common.transferMoney')}
                          inputProps={amountInputProps}
                          fullWidth
                          required
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Typography>{`${t('common.memo')}`}</Typography>
                    <FormField<FormType, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="memo"
                      multiline
                      fullWidth
                      type="text"
                      margin="normal"
                      variant="outlined"
                      InputProps={memoInputProps}
                      FormHelperTextProps={memoFormHelperTextProps}
                    />
                  </Grid>
                  <CodeInput />
                  <Grid item>
                    <RequiredText />
                  </Grid>
                </React.Fragment>
                  )
                : (
                <Box display="flex" justifyContent="center" padding={1} overflow="auto">
                  <Typography variant="h3">
                    {t('common.noData')}
                  </Typography>
                </Box>
                  )}
            </Grid>
          </LoadingAndErrorFrame>
        </Box>
      </Paper>
      <Box paddingTop={2}>
        <Grid container direction="row" justifyContent="flex-end" spacing={2}>
          <Grid item>
            <Button
              onClick={handleDebouncedBack}
              className={classes.greyButton}
            >
              {t('common.cancel')}
            </Button>
          </Grid>
          <Grid item>
            <FormSubmitButton
              context={FormContext}
              component={Button}
              disabled={loading}
              type="submit"
              className={classes.purpleGradualButton}
            >
              {loading
                ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size={24} />
                </Box>
                  )
                : t('common.confirmCreate')}
            </FormSubmitButton>
          </Grid>
        </Grid>
      </Box>
    </FormStateProvider>
  )
}

export default React.memo(AccountAgentWalletCreateForm)
